import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard } from './core/guards/admin.guard';
import { maintenanceGuard } from './core/guards/maintenance.guard';
import { featureFlagGuard } from './core/guards/feature-flag.guard';

/**
 * Application Routes
 * Defines all navigation paths with lazy loading and guards
 */
export const routes: Routes = [
  // Root redirect to client onboarding (public landing page)
  {
    path: '',
    redirectTo: 'client-onboarding',
    pathMatch: 'full',
  },

  // Maintenance page (accessible when maintenanceMode = true)
  {
    path: 'maintenance',
    loadComponent: () =>
      import('./maintenance/maintenance.component').then((m) => m.MaintenanceComponent),
  },

  // Design System Preview (dev reference)
  {
    path: 'design-system',
    loadComponent: () =>
      import('./design-system/design-system.component').then((m) => m.DesignSystemComponent),
  },

  // Public routes (Auth)
  {
    path: 'auth/login',
    loadComponent: () => import('./auth/login/login.component').then((m) => m.LoginComponent),
    canActivate: [maintenanceGuard],
  },
  {
    path: 'auth/register',
    loadComponent: () =>
      import('./auth/register/register.component').then((m) => m.RegisterComponent),
    // Internal users only: creation is restricted to administrators (backend enforces it too).
    canActivate: [maintenanceGuard, adminGuard],
  },

  // Public Client Onboarding (no authentication required)
  {
    path: 'client-onboarding',
    loadComponent: () =>
      import('./client-onboarding/client-onboarding.component').then(
        (m) => m.ClientOnboardingComponent
      ),
    canActivate: [maintenanceGuard, featureFlagGuard('clientOnboarding')],
  },

  // Public New Client Onboarding (Tenant QR / referral, personal only)
  {
    path: 'new-client',
    loadComponent: () =>
      import('./new-client/new-client.component').then(
        (m) => m.NewClientComponent
      ),
    canActivate: [maintenanceGuard],
  },

  // Merchant Onboarding (public – professional enterprise KYB form)
  {
    path: 'merchant-onboarding',
    loadComponent: () =>
      import('./merchant-onboarding/merchant-onboarding.component').then(
        (m) => m.MerchantOnboardingComponent
      ),
    canActivate: [maintenanceGuard, featureFlagGuard('merchantOnboarding')],
  },

  // Legal texts (terms, privacy, acceptable use, automated analysis, biometrics)
  {
    path: 'legal/:doc',
    loadComponent: () => import('./legal/legal-page.component').then((m) => m.LegalPageComponent),
  },

  // Flow Complete (confirmation page after verification)
  {
    path: 'flow-complete',
    loadComponent: () =>
      import('./flow-complete/flow-complete.component').then((m) => m.FlowCompleteComponent),
    canActivate: [maintenanceGuard],
  },

  // KYB Complete (confirmation page after business verification)
  {
    path: 'kyb-complete',
    loadComponent: () =>
      import('./kyb-complete/kyb-complete.component').then((m) => m.KybCompleteComponent),
    canActivate: [maintenanceGuard],
  },

  // Protected routes (requires authentication)
  {
    path: 'admin-portal',
    loadComponent: () =>
      import('./shared/components/layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [maintenanceGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'home',
        loadComponent: () =>
          import('./shared/components/home/home.component').then((m) => m.HomeComponent),
        canActivate: [authGuard],
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./onboarding/onboarding.component').then((m) => m.OnboardingComponent),
        canActivate: [authGuard],
      },

      // Admin routes (requires admin role)
      {
        path: 'admin',
        loadComponent: () =>
          import('./admin/admin-dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent
          ),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/users',
        loadComponent: () =>
          import('./admin/admin-dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent
          ),
        canActivate: [adminGuard],
      },

      // Workflow configuration (admin only)
      {
        path: 'admin/workflows',
        loadComponent: () =>
          import('./admin/workflows/workflows.component').then((m) => m.WorkflowsComponent),
        canActivate: [adminGuard, featureFlagGuard('workflows', '/admin-portal/admin')],
      },

      // Mono Management (admin only)
      {
        path: 'admin/mono-management',
        loadComponent: () =>
          import('./admin/admin-dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent
          ),
        canActivate: [adminGuard, featureFlagGuard('monoManagement', '/admin-portal/admin')],
      },

      // KYB Merchants admin view (admin only)
      {
        path: 'admin/kyb-merchants',
        loadComponent: () =>
          import('./admin/kyb-merchants/kyb-merchants.component').then(
            (m) => m.KybMerchantsComponent
          ),
        canActivate: [adminGuard, featureFlagGuard('kybMerchants', '/admin-portal/admin')],
      },

      // Feature Flags management (admin only)
      {
        path: 'admin/feature-flags',
        loadComponent: () =>
          import('./admin/feature-flags/feature-flags.component').then(
            (m) => m.FeatureFlagsComponent
          ),
        canActivate: [adminGuard],
      },

      // Unauthorized access
      {
        path: 'unauthorized',
        loadComponent: () =>
          import('./shared/components/unauthorized/unauthorized.component').then(
            (m) => m.UnauthorizedComponent
          ),
      },
    ],
  },

  // Wildcard route - redirect to client onboarding
  {
    path: '**',
    redirectTo: 'client-onboarding',
  },
];
